import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { requestURLs } from "../../Environment";
import { useEffect, useRef, useCallback } from "react";

type SentData = FormData;

type OnMessageHandler = (message: string) => void;
type OnErrorHandler = (error: any) => void;

function UseSentMutation(
  onMessage: OnMessageHandler,
  onError: OnErrorHandler,
  cancel: boolean,
  setCancel: (cancel: boolean) => void
): UseMutationResult<any, any, SentData> {
  const controllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);

  // Cleanup logic for abort and reader
  const cleanup = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      console.log('====================================');
      console.log(controllerRef.current.signal);
      console.log('====================================');
      controllerRef.current = null;
    }

    if (readerRef.current) {
      readerRef.current.cancel().catch((err) =>
        console.warn("Error canceling reader:", err)
      );
      readerRef.current = null;
      setCancel(false)
    }
  }, []);

  useEffect(() => {
    if (cancel) {
      cleanup(); // Abort and cancel on cancel trigger
    }
  }, [cancel, cleanup]);

  const mutation = useMutation<any, any, SentData>({
    mutationFn: async (data: SentData) => {
      cleanup(); // Ensure any previous request is cleaned up

      // Create a new AbortController for the current request
      const controller = new AbortController();
      const signal = controller.signal;
      controllerRef.current = controller;

      try {
        const response = await fetch(requestURLs.sentLive, {
          method: "POST",
          body: data,
          signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw JSON.parse(errorText);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No readable stream found in the response.");
        }

        readerRef.current = reader; // Store the reader for cancellation
        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const { value, done: isDone } = await reader.read();
          done = isDone;

          if (value) {
            const decodedValue = decoder.decode(value, { stream: true });
            onMessage(decodedValue);
          }
        }

        reader.releaseLock(); // Release the lock when done
      } catch (error) {
        if (signal.aborted) {
          console.log("Request aborted.");
        } else {
          onError(error);
          throw error;
        }
      } finally {
        cleanup(); // Ensure cleanup after request
      }
    },
  });

  return mutation;
}

export default UseSentMutation;
